/**
 * BootstrapGate — resolves the platform bootstrap (`./bootstrap.ts`) before
 * any branded chrome renders (platform contract §5.1, ported from dink-admin
 * DNKADM-0274). Wraps `RouterProvider` in `main.tsx`, so the gate sits before
 * every route — including the unauthenticated deep-link → centralized-login
 * redirect made inside `AuthGate` — and before first paint of the app shell.
 *
 *  - **Pending:** a minimal, unbranded loading state.
 *  - **Failure:** a BLOCKING error screen (never a degraded shell), never a
 *    composed-host fallback (no certificate exists for one), with a retry
 *    that re-issues the request. Copy is chosen by status and, when the body
 *    was JSON, tmm-api's `error.code` (H §3): `404 HOSTNAME_NOT_REGISTERED`
 *    (this host is not a reseller console), `503 CONSOLE_NOT_CONFIGURED` (the
 *    reseller sign-in host is not verified yet — try later), anything else
 *    (`502 UPSTREAM_UNAVAILABLE`, network, a malformed or tenant body) a
 *    generic retryable failure.
 *  - **Success:** renders `children`.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { BootstrapError, resolveBootstrap } from './bootstrap';

type GateState = 'pending' | 'ready' | 'failed';

type FailureKind = 'notRegistered' | 'settingUp' | 'failed';

function failureKind(error: BootstrapError | null): FailureKind {
  if (error?.code === 'HOSTNAME_NOT_REGISTERED' || error?.status === 404) return 'notRegistered';
  if (error?.code === 'CONSOLE_NOT_CONFIGURED' || error?.status === 503) return 'settingUp';
  return 'failed';
}

const fullScreenStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '100vh',
  gap: '0.75rem',
  padding: '2rem',
  textAlign: 'center',
} as const;

export function BootstrapGate({ children }: { children: ReactNode }): ReactNode {
  const { t } = useTranslation();
  const [state, setState] = useState<GateState>('pending');
  const [error, setError] = useState<BootstrapError | null>(null);
  const started = useRef(false);

  const attempt = useCallback(() => {
    setState('pending');
    setError(null);
    resolveBootstrap().then(
      () => setState('ready'),
      (err: unknown) => {
        setError(
          err instanceof BootstrapError
            ? err
            : new BootstrapError('Bootstrap failed unexpectedly.', null),
        );
        setState('failed');
      },
    );
  }, []);

  useEffect(() => {
    // Guard against React StrictMode's dev double-invoke — one real attempt
    // per mount, mirroring AuthGate's `started` ref.
    if (started.current) return;
    started.current = true;
    attempt();
  }, [attempt]);

  if (state === 'pending') {
    return (
      <div role="status" aria-live="polite" style={fullScreenStyle}>
        <p>{t('bootstrap.loading')}</p>
      </div>
    );
  }

  if (state === 'failed') {
    const kind = failureKind(error);
    return (
      <div role="alert" style={fullScreenStyle}>
        <h1>{t(`bootstrap.${kind}Title`)}</h1>
        <p style={{ maxWidth: '32rem' }}>{t(`bootstrap.${kind}Body`)}</p>
        <button type="button" onClick={attempt}>
          {t('bootstrap.retry')}
        </button>
      </div>
    );
  }

  return <>{children}</>;
}
